"use client"

import { useCallback, useEffect } from "react"
import { usePathname, useRouter, useSearchParams } from '@/platform/navigation'
import type { ReportCategory } from "@/lib/report-catalog"
import { categoryFromReportPath, reportsHubHref } from "@/lib/reports-nav"
import { isComboboxExpanded } from "@/lib/keyboard-workflow"
import { reportFilterReturnHref } from "@/lib/report-filter-state"

type CloseReportOptions = {
  /** When true, Esc closes fullscreen first instead of exiting the report. */
  screenOpen?: boolean
  onCloseScreen?: () => void
}

function useReportEscapeKey(options: {
  onClose: () => void
  screenOpen?: boolean
  onCloseScreen?: () => void
}) {
  const { onClose, screenOpen = false, onCloseScreen } = options

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return
      if (isComboboxExpanded(e.target)) return
      e.preventDefault()
      if (screenOpen && onCloseScreen) onCloseScreen()
      else onClose()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [onClose, screenOpen, onCloseScreen])
}

/** Navigate back from screen view to filter page, or from filter page to reports hub. */
export function useCloseReport(category?: ReportCategory | null, opts?: CloseReportOptions) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const close = useCallback(() => {
    if (opts?.screenOpen && opts?.onCloseScreen) {
      opts.onCloseScreen()
      return
    }
    if (pathname.endsWith("/view") || pathname.endsWith("/screen")) {
      router.push(reportFilterReturnHref(pathname, searchParams))
      return
    }
    const cat = category ?? categoryFromReportPath(pathname)
    router.push(reportsHubHref(cat))
  }, [router, pathname, category, opts, searchParams])

  useReportEscapeKey({
    onClose: close,
    screenOpen: opts?.screenOpen,
    onCloseScreen: opts?.onCloseScreen,
  })

  return close
}
